<template>
  <div class="w-full">
    <div class="w-full bg-app-gray-900 p-5">
      <Alerts
        v-if="pendingInvoices.length > 0"
        variant="danger"
        :classes="{ heading: 'max-sm:text-sm max-sm:font-semibold', message: 'max-sm:text-xs' }"
        show-icon
        class="mt-4"
        :heading="`You have (${pendingInvoices.length}) outstanding invoices that are overdue.`"
      >
        <template #alertMessage>
          <div class="text-sm my-2">Please make payment by adding a card or update your card.</div>
          <Button size="xs" class="!bg-app-red-700" @click="handlePayInvoice">
            <span>Pay Invoice</span>
            <Icon name="arrow-narrow-right-solid-icon" class="ml-2" />
          </Button>
        </template>
      </Alerts>

      <h1 class="text-2xl font-semibold mt-5 text-app-gray-50 mb-4">Billing</h1>
      <Dropdown
        :model-value="tab"
        :button-props="{ variant: 'secondary', size: 'xs', class: '!text-app-gray-100 !border-app-gray-600' }"
        :label="getDropdownLabel"
        :items="getDropdownList"
        class="block sm:hidden"
        :classes="{ menuContainer: '!w-fit' }"
        @on-item-click="(item) => handleTabClick(item.value)"
      />
      <Tabs class="border-b border-app-gray-600 hidden sm:block">
        <Tab
          class="mr-9"
          :active="tab === BILLING_TABS.plans"
          :classes="{
            active: 'text-app-primary-500 py-2',
            underlineActive: '!w-full !bg-app-primary-500',
            inactive: 'px-0 bg-transparent text-app-gray-200',
          }"
          @on-click="handleTabClick(BILLING_TABS.plans)"
        >
          <Icon name="cash-solid-icon" class="mr-2" />
          {{ BILLING_TABS.plans }}
        </Tab>
        <Tab
          :active="tab === BILLING_TABS.payment"
          :classes="{
            active: 'text-app-primary-500 py-2',
            underlineActive: '!w-full !bg-app-primary-500',
            inactive: 'px-0 bg-transparent text-app-gray-200',
          }"
          @on-click="handleTabClick(BILLING_TABS.payment)"
        >
          <Icon name="credit-card-solid-icon" class="mr-2" />
          {{ BILLING_TABS.payment }}
        </Tab>
      </Tabs>
    </div>
    <router-view :selected-team-id="selectedTeam.id" />
  </div>
</template>

<script lang="ts">
import { Icon } from "@toruslabs/vue-components/Icon";
import { Tab } from "@toruslabs/vue-components/Tab";
import { Tabs } from "@toruslabs/vue-components/Tabs";
import log from "loglevel";
import { defineComponent } from "vue";

import { Alerts } from "@/components/Alerts";
import { Button } from "@/components/Button";
import { Dropdown } from "@/components/Dropdown";
import { billingModule, teamModule } from "@/store";
import { BILLING_TABS, ROUTES } from "@/utils/enums";

export default defineComponent({
  name: "Billing",
  components: { Alerts, Button, Dropdown, Tabs, Tab, Icon },
  props: {
    teamName: {
      type: String,
      required: true,
    },
  },
  setup() {
    return {
      BILLING_TABS,
      tabDropdownList: [
        { name: BILLING_TABS.payment, value: BILLING_TABS.payment, icon: "cash-solid-icon" },
        { name: BILLING_TABS.plans, value: BILLING_TABS.plans, icon: "credit-card-solid-icon" },
      ],
    };
  },
  computed: {
    selectedTeam() {
      return teamModule.selectedTeamGetter(this.teamName);
    },
    getDropdownLabel() {
      return this.tabDropdownList.find((tab) => tab.value === this.tab)?.name;
    },
    getDropdownList() {
      const list = [
        { name: BILLING_TABS.payment, value: BILLING_TABS.payment, icon: "cash-solid-icon" },
        { name: BILLING_TABS.plans, value: BILLING_TABS.plans, icon: "credit-card-solid-icon" },
      ];
      return list;
    },
    pendingInvoices() {
      return billingModule.getPendingInvoices(this.selectedTeam.id);
    },
    tab() {
      switch (this.$route.name) {
        case ROUTES.TEAM_BILLING_PLAN_AND_SUBSCRIPTION:
        case ROUTES.TEAM_BILLING_UPGRADE_SUCCESS:
        case ROUTES.TEAM_BILLING_DOWNGRADE_SUCCESS:
          return BILLING_TABS.plans;
        case ROUTES.TEAM_BILLING_PAYMENT:
          return BILLING_TABS.payment;
        default:
          return BILLING_TABS.plans;
      }
    },
  },
  methods: {
    handleTabClick(tabName: string) {
      switch (tabName) {
        case BILLING_TABS.plans:
          this.$router.push({ name: ROUTES.TEAM_BILLING_PLAN_AND_SUBSCRIPTION });
          break;
        case BILLING_TABS.payment:
          this.$router.push({ name: ROUTES.TEAM_BILLING_PAYMENT });
          break;
        default:
          throw new Error(`Invalid tab name: ${tabName}`);
      }
    },
    async handlePayInvoice() {
      const invoice = this.pendingInvoices[0];
      try {
        const thirtyDays = 30 * 24 * 60 * 60 * 1000;
        if (invoice.due_date && new Date(invoice.due_date) <= new Date(Date.now() - thirtyDays)) {
          // payment link is expired 30 days after due date, so we need to generate a new one
          const invoiceLinks = billingModule.getInvoiceLinks(invoice.team_id, invoice.invoice_id);
          if (!invoiceLinks) {
            await billingModule.fetchInvoiceLinks({ invoiceId: invoice.invoice_id, teamId: invoice.team_id });
          }
        }
      } catch (error) {
        log.error("Error fetching invoice links", error);
      }

      const invoiceLinks = billingModule.getInvoiceLinks(invoice.team_id, invoice.invoice_id);
      const paymentUrl = invoiceLinks?.payment_url || invoice.payment_url;
      window.open(paymentUrl, "_blank", "noopener, noreferrer");
    },
  },
});
</script>
